import React from "react";

import { InfoIcon as Info, Typography } from "@packages/ds";
import { useIntl } from "react-intl";

import PageMeta from "Components/PageMeta";
import { Button, IconControl } from "ds/Button";
import Dialog from "ds/Dialog";
import * as Table from "ds/Table";
import useDecodedParams from "Hooks/useDecodedParams";
import useSelectorWithUrlParams from "Hooks/useSelectorWithUrlParams";
import getUrl from "Libs/getUrl";
import { gitProjectSelector } from "Reducers/project/project";
import { backupEnvTypesInfoSelector } from "Reducers/projectSettings";

import * as S from "./BackupSchedules.styles";

const BackupSchedules = () => {
  const intl = useIntl();

  const { organizationId, projectId } = useDecodedParams<{
    organizationId: string;
    projectId: string;
    environmentId: string;
  }>();

  const project = useSelectorWithUrlParams(gitProjectSelector);

  const backupEnvTypesInfo = useSelectorWithUrlParams(
    backupEnvTypesInfoSelector
  );

  return (
    <S.Section>
      <PageMeta title={`Backup schedules | ${project?.title}`} />
      <S.Title tag="h2">
        {intl.formatMessage({
          id: "settings.backupSchedules.title",
          defaultMessage: "Backup schedules"
        })}
      </S.Title>
      <Typography.BodyText>
        {intl.formatMessage({
          id: "settings.backupSchedules.description",
          defaultMessage:
            "Configure your backup schedules for each environment type for this project."
        })}
      </Typography.BodyText>
      <S.InfoTextWrapper>
        <S.HelpIcon />
        <Typography.BodyText>
          {intl.formatMessage({
            id: "settings.backupSchedules.more_info",
            defaultMessage: "Learn more about Backup schedules in our"
          })}{" "}
          <Button
            analyticId="documentation"
            variant="link"
            href={intl.formatMessage({
              id: "links.documentation.environments.backup"
            })}
          >
            {intl.formatMessage({ id: "documentation" })}
          </Button>
          .
        </Typography.BodyText>
      </S.InfoTextWrapper>
      <Table.Table>
        <S.TableThead>
          <Table.Th width="32%">
            {intl.formatMessage({
              id: "settings.backupSchedules.backup.environment",
              defaultMessage: "Environment type"
            })}
          </Table.Th>
          <Table.Th width="40%">
            {intl.formatMessage({
              id: "settings.backupSchedules.backup.frequency",
              defaultMessage: "Frequency"
            })}
          </Table.Th>
          <Table.Th width="20%">
            <S.ToolTipWrapper>
              {intl.formatMessage({
                id: "settings.backupSchedules.backup.total",
                defaultMessage: "Total Backups"
              })}
              <Dialog
                trigger={
                  <IconControl>
                    <Info />
                  </IconControl>
                }
                title={intl.formatMessage({
                  id: "settings.backupSchedules.tooltip.title",
                  defaultMessage: "Total backups"
                })}
                description={intl.formatMessage({
                  id: "settings.backupSchedules.tooltip.description"
                })}
                link={
                  <Button
                    variant="link"
                    analyticId="learnmore"
                    href={intl.formatMessage({
                      id: "links.documentation.environments.backup.set_backup_limit"
                    })}
                  >
                    {intl.formatMessage({ id: "learnmore" })}
                  </Button>
                }
              />
            </S.ToolTipWrapper>
          </Table.Th>
          <Table.Th width="8%">&nbsp;</Table.Th>
        </S.TableThead>
        <Table.Tbody>
          {backupEnvTypesInfo.map(env => (
            <S.TableTr key={env.type}>
              <S.TableTd width="32%" weight="semiBold">
                {env.type}
              </S.TableTd>
              <Table.Td width="40%">{env.frequency}</Table.Td>
              <Table.Td width="20%">{env.backups || null}</Table.Td>
              <Table.Td width="8%">
                <Button
                  variant="link"
                  analyticId="settings.backupSchedules.manage_link"
                  to={getUrl({
                    key: "organization.project.backupSchedules",
                    props: {
                      organizationId,
                      projectId,
                      environmentType: env.type
                    }
                  })}
                >
                  {intl.formatMessage({
                    id: "settings.backupSchedules.manage_link",
                    defaultMessage: "Manage"
                  })}
                </Button>
              </Table.Td>
            </S.TableTr>
          ))}
        </Table.Tbody>
      </Table.Table>
    </S.Section>
  );
};

export default BackupSchedules;
